import { Route } from '@angular/router';
import { authGuard, onboardingGuard } from '@olonera/auth';
import { AuthenticatedLayoutComponent } from './features/authenticated-layout/authenticated-layout';
import { PageNotFoundComponent, AccessDeniedComponent } from '@olonera/ui';
import {
  PlanListComponent,
  PlanFormComponent,
  PlanDetailComponent
} from '@olonera/treatment-plans';

export const appRoutes: Route[] = [
    {
        path: 'auth',
        loadChildren: () => import('@olonera/auth').then(m => m.AUTH_ROUTES),
        data: { isPractitionerApp: true }
    },
    {
        path: 'profile-setup',
        canActivate: [authGuard, onboardingGuard],
        loadComponent: () => import('./features/profile-setup/profile-setup').then(m => m.ProfileSetupComponent)
    },
    {
        path: '',
        canActivate: [authGuard],
        component: AuthenticatedLayoutComponent,
        children: [
            {
                path: 'dashboard',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/dashboard/dashboard').then(m => m.DashboardComponent)
            },
            {
                path: 'members',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/patient-management/patient-management').then(m => m.PatientManagementComponent)
            },
            {
                path: 'members/:id',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/patient-management/patient-details/patient-details.component').then(m => m.PatientDetailsComponent)
            },
            {
                path: 'templates',
                canActivate: [onboardingGuard],
                children: [
                    {
                        path: '',
                        loadComponent: () => import('./features/prescription-templates/prescription-templates').then(m => m.PrescriptionTemplatesComponent)
                    },
                    {
                        path: 'new',
                        loadComponent: () => import('./features/prescription-templates/prescription-template-form').then(m => m.PrescriptionTemplateFormComponent)
                    },
                    {
                        path: ':id/edit',
                        data: { action: 'edit' },
                        loadComponent: () => import('./features/prescription-templates/prescription-template-form').then(m => m.PrescriptionTemplateFormComponent)
                    }
                ]
            },
            {
                path: 'messages',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/messages/messages.page').then(m => m.MessagesPage)
            },
            {
                path: 'resources',
                loadChildren: () => import('@olonera/practitioner-resources').then(m => m.RESOURCES_ROUTES)
            },
            {
                path: 'plans',
                canActivate: [onboardingGuard],
                children: [
                    {
                        path: '',
                        loadComponent: () => import('@olonera/treatment-plans').then(m => m.PlanListComponent)
                    },
                    {
                        path: 'create',
                        loadComponent: () => import('@olonera/treatment-plans').then(m => m.PlanFormComponent)
                    },
                    {
                        path: ':id',
                        loadComponent: () => import('@olonera/treatment-plans').then(m => m.PlanDetailComponent)
                    },
                    {
                        path: ':id/edit',
                        loadComponent: () => import('@olonera/treatment-plans').then(m => m.PlanFormComponent)
                    },
                    {
                        path: ':id/history',
                        loadComponent: () => import('./features/plan-history-page/plan-history-page.component').then(m => m.PlanHistoryPageComponent)
                    }
                ]
            },
            {
                path: 'calendar',
                canActivate: [onboardingGuard],
                children: [
                    {
                        path: '',
                        loadComponent: () => import('./features/calendar/pages/calendar-view/calendar-view').then(m => m.CalendarViewComponent)
                    },
                    {
                        path: 'settings',
                        loadComponent: () => import('./features/calendar/pages/calendar-settings/calendar-settings').then(m => m.CalendarSettingsComponent)
                    },
                    {
                        path: 'appointments/:id',
                        loadComponent: () => import('./features/calendar/pages/calendar-view/calendar-view').then(m => m.CalendarViewComponent)
                    },
                    {
                        path: ':id',
                        loadComponent: () => import('./features/calendar/pages/calendar-view/calendar-view').then(m => m.CalendarViewComponent)
                    }
                ]
            },
            {
                path: 'appointments',
                canActivate: [onboardingGuard],
                children: [
                    {
                        path: '',
                        loadComponent: () => import('./features/calendar/pages/calendar-view/calendar-view').then(m => m.CalendarViewComponent)
                    },
                    {
                        path: ':id',
                        loadComponent: () => import('./features/calendar/pages/calendar-view/calendar-view').then(m => m.CalendarViewComponent)
                    }
                ]
            },
            {
                path: 'marketplace',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/marketplace-sso/marketplace-sso.component').then(m => m.MarketplaceSsoComponent)
            },
            {
                path: 'financials',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/financials/financials').then(m => m.FinancialsComponent)
            },
            {
                path: 'settings',
                canActivate: [onboardingGuard],
                loadComponent: () => import('./features/settings/settings-layout').then(m => m.SettingsLayoutComponent),
                children: [
                    {
                        path: '',
                        redirectTo: 'profile',
                        pathMatch: 'full'
                    },
                    {
                        path: 'profile',
                        loadComponent: () => import('./features/settings/profile-settings').then(m => m.ProfileSettingsComponent)
                    },
                    {
                        path: 'practice',
                        loadComponent: () => import('./features/settings/practice-settings').then(m => m.PracticeSettingsComponent)
                    },
                    {
                        path: 'security',
                        loadComponent: () => import('./features/settings/security/security-settings.component').then(m => m.SecuritySettingsComponent)
                    },
                    {
                        path: 'notifications',
                        loadComponent: () => import('./features/settings/notifications-settings').then(m => m.NotificationsSettingsComponent)
                    },
                    {
                        path: 'integrations',
                        loadComponent: () => import('./features/settings/integrations-settings').then(m => m.IntegrationsSettingsComponent)
                    },
                    {
                        path: 'privacy',
                        loadComponent: () => import('@olonera/auth').then(m => m.ConsentSettingsComponent)
                    }
                ]
            },
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
        ]
    },
    { path: '**', component: PageNotFoundComponent }
];
